import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { X, Mic, Pause, Square, RotateCcw, Send, FileText, MapPin } from 'lucide';
import { api } from '../api.js';
import { lucideIcon } from '../icon.js';
import { confirmDialog } from '../utils/confirm-dialog.js';
import { naarSpraakWav } from '../utils/spraak-wav.js';

/**
 * Een boring inspreken met de microfoon van de computer, net als in de veldapp: één grote knop
 * (Opnemen → Pauze → Hervat) met een teller en een knop 'Stop en verwerk' -- geen tussenscherm.
 * Mislukt het verwerken (bv. geen verbinding), dan blijft de opname bewaard en kun je het opnieuw
 * proberen. Verwerken levert een bestand op dat langs exact dezelfde weg gaat als 'Via audio'
 * (project-detail → uploadAdditionalFiles): zelfde upload, zelfde transcriptie en extractie.
 *
 * De microfoon in de browser kan op meer manieren mislukken dan in de app, en dan moet de
 * gebruiker weten wat hij moet doen:
 *  - de toestemmingsvraag komt pas bij de klik op 'Opnemen' (zonder klik onderdrukken browsers
 *    hem soms stil);
 *  - eerder geweigerd → dat zien we vooraf (Permissions API) en zeggen waar je het aanzet;
 *  - geen microfoon, of bezet door een ander programma (Teams, Zoom) → eigen melding;
 *  - geen beveiligde pagina (https/localhost) → de browser geeft de microfoon dan nooit;
 *  - een niveaumeter tijdens het opnemen: zo zie je dat er echt geluid binnenkomt, en niet pas
 *    na afloop dat de opname stil was;
 *  - na afloop of bij sluiten gaat de microfoon uit (het opname-icoon van de browser verdwijnt).
 *
 * Events: `verwerk` (detail: { file }) en `sluit`. Eigenschap `bezig`: de ouder is aan het
 * uploaden.
 */
export class BoringInsprekenDialog extends LitElement {
  static properties = {
    open: { type: Boolean },
    bezig: { type: Boolean },
    /** Extractiemodel van het project ('nen5104'/'nen6693'): bepaalt welke instructies je ziet. */
    profiel: { type: String },
    _instructies: { state: true },     // HTML van de opname-instructies van het bedrijf, of ''
    _toonInstructies: { state: true },
    _locatie: { state: true },         // { x, y, altitude, gpsAccuracy, gpsVerticalAccuracy } of null
    _locatieStatus: { state: true },   // 'bepalen' | 'ok' | 'geweigerd' | 'onbekend'
    _fase: { state: true },        // 'klaar' | 'opnemen' | 'pauze' | 'verwerken' | 'mislukt'
    _ms: { state: true },
    _niveau: { state: true },
    _fout: { state: true },
    _toestemming: { state: true }, // 'granted' | 'denied' | 'prompt' | null (onbekend)
    _speelt: { state: true },
    _positie: { state: true },
  };

  constructor() {
    super();
    this.open = false;
    this.bezig = false;
    this.profiel = 'nen5104';
    this._instructies = '';
    this._toonInstructies = false;
    this._locatie = null;
    this._locatieStatus = 'bepalen';
    this._reset();
    this._toestemming = null;
  }

  _reset() {
    this._fase = 'klaar';
    this._ms = 0;
    this._niveau = 0;
    this._fout = null;
    this._speelt = false;
    this._positie = 0;
    this._bestand = null;
    if (this._url) URL.revokeObjectURL(this._url);
    this._url = null;
  }

  updated(changed) {
    if (changed.has('open') && this.open) { this._controleerToestemming(); this._laadInstructies(); this._startLocatie(); }
    if (changed.has('open') && !this.open) { this._stopMicrofoon(); this._stopLocatie(); this._reset(); }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stopMicrofoon();
    this._stopLocatie();
  }

  /*
   * LOCATIE. Net als in de veldapp: onderin de positie, die met de opname meegaat en de boring zijn
   * coördinaten geeft. De browser vraagt hier om toestemming. Weigert de gebruiker (of kan de
   * browser het niet), dan blijft de locatie leeg en krijgt de boring geen coördinaten -- verder
   * werkt alles gewoon. We blijven de positie volgen tot het venster sluit: de eerste schatting
   * is vaak grof en wordt binnen een paar seconden nauwkeuriger.
   */
  _startLocatie() {
    this._stopLocatie();
    this._locatie = null;
    this._locatieReden = '';
    if (!navigator.geolocation || !window.isSecureContext) {
      this._locatieStatus = 'onbekend';
      this._locatieReden = 'Deze browser of pagina kan de locatie niet opvragen (alleen via https of localhost).';
      return;
    }
    this._locatieStatus = 'bepalen';
    const gelukt = (pos) => {
      const c = pos.coords;
      // Alleen vervangen door een nauwkeuriger (of eerste) meting.
      if (this._locatie && this._locatie.gpsAccuracy != null && c.accuracy > this._locatie.gpsAccuracy) return;
      // Hoogte alleen als hij echt gemeten is. Een laptop heeft geen GPS: de positie komt via wifi
      // en de browser geeft dan toch een hoogte van 0 door. De backend rekent een hoogte om naar
      // NAP (geoïde, ~43 m in Nederland), en zo werd een kantoor in Amsterdam -42,98 m NAP. Liever
      // geen maaiveldhoogte dan een verzonnen.
      const hoogteBetrouwbaar = c.altitude != null && c.altitude !== 0
        && Number.isFinite(c.altitudeAccuracy) && c.altitudeAccuracy > 0 && c.altitudeAccuracy <= 10;
      this._locatie = {
        x: c.longitude, y: c.latitude,
        altitude: hoogteBetrouwbaar ? c.altitude : null,
        gpsAccuracy: c.accuracy ?? null,
        gpsVerticalAccuracy: hoogteBetrouwbaar ? c.altitudeAccuracy : null,
      };
      this._locatieStatus = 'ok';
    };
    const mislukt = (err) => {
      if (this._locatie) return; // er is al een positie; een latere fout negeren
      const mac = /Mac/i.test(navigator.platform || navigator.userAgent);
      if (err?.code === 1) {
        this._locatieStatus = 'geweigerd';
        this._locatieReden = 'De browser mag de locatie niet gebruiken. Zet het aan via het slotje links in de adresbalk.';
      } else {
        // Toestemming is er wel, maar er komt geen positie. Bijna altijd: het besturingssysteem
        // geeft hem niet door (macOS: Locatievoorzieningen voor de browser uit), of wifi staat
        // uit (zonder GPS bepaalt een laptop zijn positie via wifi). We blijven het proberen.
        this._locatieStatus = 'onbekend';
        this._locatieReden = mac
          ? 'Er komt geen positie binnen. Controleer Systeeminstellingen → Privacy en beveiliging → Locatievoorzieningen (aan, en aan voor deze browser) en of wifi aan staat.'
          : 'Er komt geen positie binnen. Controleer of locatievoorzieningen van het besturingssysteem aan staan en of wifi aan staat.';
      }
    };
    // Eerst snel en grof (wifi/netwerk), dan blijven verfijnen. Geen time-out op het volgen: komt
    // de positie later alsnog binnen (bv. na het aanzetten in de systeeminstellingen), dan
    // verschijnt hij gewoon.
    navigator.geolocation.getCurrentPosition(gelukt, mislukt, { enableHighAccuracy: false, maximumAge: 60000, timeout: 15000 });
    this._volg = navigator.geolocation.watchPosition(gelukt, (err) => { if (err?.code === 1) mislukt(err); }, { enableHighAccuracy: true, maximumAge: 10000 });
  }

  _stopLocatie() {
    if (this._volg != null) navigator.geolocation?.clearWatch(this._volg);
    this._volg = null;
  }

  _renderLocatie() {
    if (this._locatieStatus === 'ok' && this._locatie) {
      const nauw = this._locatie.gpsAccuracy;
      const kleur = nauw == null ? 'var(--mv-text-muted)' : nauw < 10 ? '#16a34a' : nauw < 30 ? '#f59e0b' : '#dc2626';
      return html`<div class="locatie"><span style="color:${kleur}">${unsafeSVG(lucideIcon(MapPin, 15))}</span>
        ${this._locatie.y.toFixed(5)}, ${this._locatie.x.toFixed(5)}${nauw != null ? html` ±${Math.round(nauw)}m` : ''}</div>`;
    }
    if (this._locatieStatus === 'bepalen') return html`<div class="locatie">${unsafeSVG(lucideIcon(MapPin, 15))} Locatie bepalen…</div>`;
    const tekst = this._locatieStatus === 'geweigerd' ? 'Geen toegang tot locatie' : 'Locatie niet beschikbaar';
    return html`<div class="locatie dim" title=${this._locatieReden || ''}>${unsafeSVG(lucideIcon(MapPin, 15))} ${tekst}</div>`;
  }

  /** Dezelfde opname-instructies als in de veldapp: van het bedrijf, voor het model van dit project. */
  async _laadInstructies() {
    this._toonInstructies = false;
    try {
      const res = await api.getRecordingInstructions(this.profiel || 'nen5104');
      this._instructies = typeof res?.instructions === 'string' ? res.instructions : '';
    } catch (_) {
      this._instructies = '';
    }
  }

  /*
   * De instructies zijn HTML die een beheerder zelf opmaakt. We tonen ze in een iframe met
   * sandbox (zonder scripts): zo kan er nooit iets in die tekst in de backoffice zelf draaien.
   */
  _instructieDocument() {
    return `<!doctype html><html><head><meta charset="utf-8"><style>
      body { font: 15px/1.55 -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1f2937; margin: 0; padding: 4px 2px; }
      h1, h2, h3 { line-height: 1.25; } img { max-width: 100%; } table { border-collapse: collapse; } td, th { border: 1px solid #e5e7eb; padding: 4px 8px; }
    </style></head><body>${this._instructies}</body></html>`;
  }

  /** Vooraf kijken of de microfoon al geweigerd is, zodat we het meteen kunnen uitleggen. */
  async _controleerToestemming() {
    this._fout = null;
    if (!window.isSecureContext) {
      this._fout = 'De microfoon werkt alleen op een beveiligde pagina (https). Open de backoffice via https.';
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      this._fout = 'Deze browser kan niet opnemen. Gebruik een recente versie van Chrome, Edge, Firefox of Safari.';
      return;
    }
    try {
      const status = await navigator.permissions?.query({ name: 'microphone' });
      if (status) {
        this._toestemming = status.state;
        if (status.state === 'denied') this._fout = this._uitlegGeweigerd();
        status.onchange = () => { this._toestemming = status.state; if (status.state !== 'denied') this._fout = null; };
      }
    } catch (_) { this._toestemming = null; /* Safari kent deze vraag niet; dan horen we het bij de klik */ }
  }

  _uitlegGeweigerd() {
    const mac = /Mac/i.test(navigator.platform || navigator.userAgent);
    return `Geen toegang tot de microfoon. Klik op het slotje (of het microfoon-icoon) links in de adresbalk, zet Microfoon op "Toestaan" en probeer het opnieuw.${mac ? ' Werkt dat niet: op een Mac moet de browser ook toestemming hebben in Systeeminstellingen → Privacy en beveiliging → Microfoon.' : ''}`;
  }

  async _start() {
    this._fout = null;
    let stream;
    try {
      // De vraag om toestemming verschijnt hier, direct na de klik.
      // Mono: twee kanalen voegen voor spraak niets toe (we mengen ze toch naar één).
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 } });
    } catch (e) {
      const naam = e?.name || '';
      if (naam === 'NotAllowedError' || naam === 'SecurityError') { this._toestemming = 'denied'; this._fout = this._uitlegGeweigerd(); }
      else if (naam === 'NotFoundError' || naam === 'OverconstrainedError') this._fout = 'Er is geen microfoon gevonden. Sluit er een aan of controleer de geluidsinstellingen.';
      else if (naam === 'NotReadableError' || naam === 'AbortError') this._fout = 'De microfoon is bezet, waarschijnlijk door een ander programma (Teams, Zoom). Sluit dat en probeer opnieuw.';
      else this._fout = `Opnemen lukt niet: ${e?.message || naam || 'onbekende fout'}.`;
      return;
    }
    this._toestemming = 'granted';
    this._stream = stream;
    const type = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
      .find((t) => MediaRecorder.isTypeSupported?.(t)) || '';
    const recorder = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    this._stukken = [];
    recorder.ondataavailable = (ev) => { if (ev.data?.size) this._stukken.push(ev.data); };
    recorder.onstop = () => this._naStop(recorder.mimeType || type);
    this._recorder = recorder;

    // Niveaumeter
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      this._ctx = new Ctx();
      const bron = this._ctx.createMediaStreamSource(stream);
      const analyser = this._ctx.createAnalyser();
      analyser.fftSize = 512;
      bron.connect(analyser);
      const data = new Uint8Array(analyser.fftSize);
      const meet = () => {
        if (!this._ctx) return;
        analyser.getByteTimeDomainData(data);
        let piek = 0;
        for (const v of data) piek = Math.max(piek, Math.abs(v - 128));
        this._niveau = this._fase === 'opnemen' ? Math.min(1, piek / 64) : 0;
        this._meetFrame = requestAnimationFrame(meet);
      };
      meet();
    } catch (_) { /* zonder meter kan het ook */ }

    recorder.start(1000);
    this._fase = 'opnemen';
    this._telVanaf = Date.now();
    this._telBasis = 0;
    this._teller = setInterval(() => { if (this._fase === 'opnemen') this._ms = this._telBasis + (Date.now() - this._telVanaf); }, 200);
  }

  _hoofdknop() {
    if (this._fase === 'klaar') return this._start();
    if (this._fase === 'opnemen') {
      this._recorder?.pause();
      this._telBasis = this._ms;
      this._fase = 'pauze';
    } else if (this._fase === 'pauze') {
      this._recorder?.resume();
      this._telVanaf = Date.now();
      this._fase = 'opnemen';
    }
  }

  _stop() {
    if (this._recorder && this._recorder.state !== 'inactive') this._recorder.stop();
  }

  async _naStop(mimeType) {
    let mime = (mimeType || 'audio/webm').split(';')[0];
    let ext = mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm';
    let blob = new Blob(this._stukken || [], { type: mime });
    this._stopMicrofoon();
    if (!blob.size) { this._fase = 'klaar'; this._fout = 'De opname is leeg. Probeer het opnieuw.'; return; }
    // Naar 16 kHz mono wav: de browser neemt op op 48 kHz, en daarop transcribeert het model
    // aantoonbaar slechter (zie utils/spraak-wav.js). Lukt omzetten niet, dan het origineel.
    try {
      blob = await naarSpraakWav(blob);
      mime = 'audio/wav';
      ext = 'wav';
    } catch (e) {
      console.warn('Omzetten naar 16 kHz wav mislukt; origineel wordt geüpload.', e);
    }
    // Unieke naam: de backend slaat een opname met een bestandsnaam die al in het project staat
    // over (dat is voor herhaalde uploads uit de veldapp).
    this._bestand = new File([blob], `ingesproken_${new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14)}.${ext}`, { type: mime });
    this._url = URL.createObjectURL(blob);
    // 'Stop en verwerk': geen tussenscherm, meteen door naar de verwerking.
    this._verwerk();
  }

  _stopMicrofoon() {
    clearInterval(this._teller);
    if (this._meetFrame) cancelAnimationFrame(this._meetFrame);
    this._meetFrame = null;
    try { if (this._recorder && this._recorder.state !== 'inactive') { this._recorder.onstop = null; this._recorder.stop(); } } catch (_) {}
    this._recorder = null;
    this._stream?.getTracks().forEach((t) => t.stop());
    this._stream = null;
    this._ctx?.close?.();
    this._ctx = null;
    this._niveau = 0;
  }

  _opnieuw() {
    this._stopMicrofoon();
    this._reset();
  }

  /**
   * De opname naar de ouder (die hem uploadt zoals 'Via audio'). Mislukt dat, dan blijft de opname
   * hier bewaard en kun je het opnieuw proberen -- een ingesproken boring mag niet verloren gaan
   * omdat de verbinding even wegviel.
   */
  _verwerk() {
    if (!this._bestand) return;
    this._fase = 'verwerken';
    this._fout = null;
    this.dispatchEvent(new CustomEvent('verwerk', {
      detail: {
        file: this._bestand,
        locatie: this._locatie,
        klaar: (fout) => {
          if (!fout) return; // de ouder sluit het venster
          this._fase = 'mislukt';
          this._fout = `Verwerken is niet gelukt: ${fout}. De opname is bewaard; probeer het opnieuw.`;
        },
      },
      bubbles: true, composed: true,
    }));
  }

  async _sluit() {
    if (this._fase === 'verwerken') return; // de upload loopt; even wachten
    if (this._fase === 'opnemen' || this._fase === 'pauze' || this._fase === 'mislukt') {
      const ok = await confirmDialog({
        title: 'Opname weggooien?',
        message: 'Je opname is nog niet verwerkt. Sluit je het venster, dan gaat hij verloren.',
        confirmLabel: 'Weggooien',
        danger: true,
      });
      if (!ok) return;
    }
    this._stopMicrofoon();
    this._reset();
    this.dispatchEvent(new CustomEvent('sluit', { bubbles: true, composed: true }));
  }

  _tijd(ms) {
    const s = Math.floor(ms / 1000);
    return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  }

  render() {
    if (!this.open) return html``;
    const opnemend = this._fase === 'opnemen' || this._fase === 'pauze';
    const hoofdLabel = this._fase === 'klaar' ? 'Opnemen' : this._fase === 'opnemen' ? 'Pauze' : 'Hervat';
    const hoofdIcoon = this._fase === 'klaar' ? Mic : this._fase === 'opnemen' ? Pause : Mic;
    return html`
      <div class="overlay" @click=${(e) => e.target === e.currentTarget && this._sluit()}>
        <div class="kaart ${this._toonInstructies ? 'breed' : ''}" role="dialog" aria-label="Boring inspreken">
          <div class="kop">
            <h3>Boring inspreken</h3>
            <button class="icoon" @click=${this._sluit} aria-label="Sluiten">${unsafeSVG(lucideIcon(X, 20))}</button>
          </div>
          <!-- Direct onder de titel en even ver ingesprongen, niet in de (gecentreerde) opnamekolom. -->
          <p class="uitleg">
            Spreek de boring in zoals in de veldapp: naam, en per laag de diepte en de beschrijving.
            ${this._toestemming !== 'granted' ? html`<br>Bij de eerste keer vraagt de browser om toegang tot de microfoon.` : ''}
          </p>

          <div class="lijf">
          <div class="opname-kolom">
          ${this._fase === 'verwerken' ? html`
            <div class="midden">
              <div class="draaier"></div>
              <div class="opgenomen">Verwerken…</div>
              <div class="status">Opname van ${this._tijd(this._ms)} wordt geüpload en verwerkt, net als via 'Via audio'.</div>
            </div>
          ` : this._fase === 'mislukt' ? html`
            <div class="midden">
              <div class="acties" style="justify-content:center;">
                <button class="secundair" @click=${this._opnieuw}>${unsafeSVG(lucideIcon(RotateCcw, 15))} Opnieuw opnemen</button>
                <button class="primair" @click=${this._verwerk}>${unsafeSVG(lucideIcon(Send, 15))} Opnieuw proberen</button>
              </div>
            </div>
          ` : html`
            <div class="midden">
              <button class="hoofd ${this._fase}" @click=${this._hoofdknop}>
                ${unsafeSVG(lucideIcon(hoofdIcoon, 30))}
                <span class="hoofd-label">${hoofdLabel}</span>
                ${opnemend ? html`<span class="teller">${this._tijd(this._ms)}</span>` : ''}
              </button>
              ${opnemend ? html`
                <div class="meter" title="Geluidsniveau"><div style="width:${Math.round(this._niveau * 100)}%"></div></div>
                <div class="status">${this._fase === 'pauze' ? 'Gepauzeerd' : 'Aan het opnemen…'}</div>
                <button class="stop" @click=${this._stop}>${unsafeSVG(lucideIcon(Square, 16))} Stop en verwerk</button>
              ` : ''}
              ${this._instructies ? html`
                <button class="instructie-knop" @click=${() => { this._toonInstructies = !this._toonInstructies; }}>
                  ${unsafeSVG(lucideIcon(FileText, 16))} ${this._toonInstructies ? 'Instructies sluiten' : 'Instructies openen'}
                </button>
              ` : ''}
            </div>
          `}

          ${this._fout ? html`<p class="fout">${this._fout}</p>` : ''}
          ${this._renderLocatie()}
          </div>
          ${this._toonInstructies ? html`
            <!-- Rechts van de opname, zodat Opnemen/Pauze/Stop tegelijk met de instructies in beeld zijn. -->
            <div class="instructies">
              <iframe sandbox title="Opname-instructies" srcdoc=${this._instructieDocument()}></iframe>
            </div>
          ` : ''}
          </div>
        </div>
      </div>
    `;
  }

  static styles = css`
    .overlay {
      position: fixed; inset: 0; z-index: 2000; background: rgba(15, 23, 42, 0.45);
      display: flex; align-items: center; justify-content: center; padding: 16px;
    }
    .kaart {
      width: 100%; max-width: 640px; min-height: 520px; display: flex; flex-direction: column;
      background: var(--mv-surface, #fff); color: var(--mv-text);
      border-radius: 16px; box-shadow: 0 20px 50px rgba(0,0,0,0.25); padding: 26px 30px 30px;
    }
    .kop { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
    h3 { margin: 0; font-size: 1.3rem; }
    .instructie-knop {
      display: flex; align-items: center; gap: 7px; margin-top: 8px; padding: 9px 18px; border-radius: 22px;
      border: 1.5px solid var(--mv-primary, #16a34a); background: transparent; color: var(--mv-primary, #16a34a);
      font-weight: 700; font-size: 0.85rem; cursor: pointer;
    }
    .locatie { display: flex; align-items: center; justify-content: center; gap: 6px; margin-top: 14px; font-size: 0.85rem; color: var(--mv-text-muted); font-variant-numeric: tabular-nums; }
    .locatie.dim { font-style: italic; }
    .kaart { transition: max-width .2s ease; max-height: calc(100vh - 32px); }
    .kaart.breed { max-width: 1280px; }
    .lijf { flex: 1; display: flex; gap: 28px; min-height: 0; }
    .opname-kolom { flex: 0 0 auto; width: 100%; max-width: 580px; margin: 0 auto; display: flex; flex-direction: column; }
    .kaart.breed .opname-kolom { width: 580px; margin: 0; }
    .instructies { flex: 1; display: flex; flex-direction: column; min-width: 0; border-left: 1px solid var(--mv-border); padding-left: 28px; }
    .instructies iframe { flex: 1; min-height: 440px; width: 100%; border: 1px solid var(--mv-border); border-radius: 10px; background: #fff; }
    /* Smal scherm: de instructies onder de opname in plaats van ernaast. */
    @media (max-width: 1000px) {
      .lijf { flex-direction: column; overflow-y: auto; }
      .kaart.breed .opname-kolom { width: 100%; }
      .instructies { border-left: none; padding-left: 0; border-top: 1px solid var(--mv-border); padding-top: 16px; }
      .instructies iframe { min-height: 320px; }
    }
    button { font: inherit; }
    .icoon { background: none; border: 1px solid var(--mv-border); border-radius: 50%; width: 36px; height: 36px; cursor: pointer; color: var(--mv-text); display: flex; align-items: center; justify-content: center; }
    .uitleg { color: var(--mv-text-muted); font-size: 0.85rem; margin: 0 0 18px; line-height: 1.45; }
    .midden { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; padding: 10px 0; }
    .hoofd {
      width: 180px; height: 180px; border-radius: 50%; border: none; cursor: pointer; color: #fff;
      display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px;
      background: var(--mv-primary, #16a34a); box-shadow: 0 8px 20px rgba(0,0,0,0.15); transition: transform .1s;
    }
    .hoofd:active { transform: scale(0.97); }
    .hoofd.opnemen { background: #f59e0b; }
    .hoofd.pauze { background: var(--mv-primary, #16a34a); }
    .hoofd:disabled { opacity: 0.5; cursor: default; }
    .hoofd-label { font-weight: 800; font-size: 1.05rem; }
    .teller { font-weight: 600; font-variant-numeric: tabular-nums; }
    .meter { width: 280px; height: 8px; border-radius: 4px; background: var(--mv-border); overflow: hidden; }
    .meter > div { height: 100%; background: var(--mv-primary, #16a34a); transition: width .08s linear; }
    .status { font-size: 0.85rem; color: var(--mv-text-muted); }
    .stop {
      display: flex; align-items: center; gap: 8px; background: #dc2626; color: #fff; border: none;
      border-radius: 10px; padding: 10px 22px; font-weight: 700; cursor: pointer;
    }
    .opgenomen { font-weight: 700; }
    .draaier { width: 44px; height: 44px; border-radius: 50%; border: 4px solid var(--mv-border); border-top-color: var(--mv-primary, #16a34a); animation: draai 0.9s linear infinite; }
    @keyframes draai { to { transform: rotate(360deg); } }
    .speler { display: flex; align-items: center; gap: 12px; width: 100%; }
    .speel {
      width: 46px; height: 46px; border-radius: 50%; border: none; cursor: pointer; flex-shrink: 0;
      background: var(--mv-primary, #16a34a); color: #fff; display: flex; align-items: center; justify-content: center;
    }
    .voortgang { flex: 1; height: 6px; border-radius: 3px; background: var(--mv-border); overflow: hidden; }
    .voortgang > div { height: 100%; background: var(--mv-primary, #16a34a); }
    .acties { display: flex; gap: 10px; width: 100%; justify-content: flex-end; margin-top: 6px; }
    .primair, .secundair { display: flex; align-items: center; gap: 6px; border-radius: 8px; padding: 10px 18px; font-weight: 700; cursor: pointer; }
    .primair { background: var(--mv-primary, #16a34a); color: #fff; border: none; }
    .secundair { background: transparent; color: var(--mv-text); border: 1px solid var(--mv-border); }
    .primair:disabled, .secundair:disabled { opacity: 0.55; cursor: default; }
    .fout { color: #dc2626; font-size: 0.85rem; font-weight: 600; margin: 14px 0 0; line-height: 1.45; }
  `;
}

customElements.define('boring-inspreken-dialog', BoringInsprekenDialog);
